// TODO: Update for absolute pos

// Usage:
//     class ScaryEnemy extends CoolParent {
//         constructor(scanner) {
//             ...
//             this.healthBar = scanner.addBar(this.pos, this.radius + 10,
//                             this.health, this.maxHealth);
//             ...
//         }
//         ...
//         defendOrRegen() {
//             ...
//             this.healthBar.val = updatedHealth;
//             ...
//         }
//         ...
//         die() {
//             ...
//             this.healthBar.selfDestruct();
//             ...
//         }
//         ...
//     }
//     ...
//     class Space extends PIXI.Container {
//         constructor() {
//             ...
//             this.scanner = new Scanner();
//             this.addChild(this.scanner);
//             ...
//         }
//         ...
//         update() {
//             ...
//             this.scanner.redraw();
//             ...
//         }
//         ...
//         spawnScaryEnemy() {
//             ...
//             let scaryEnemy = new ScaryEnemy(this.scanner);
//             ...
//         }
//         ...
//     }

import {Container, Graphics, Sprite, Text} from "pixi.js";
import {Vector} from "./i.js"; // ./vector.js
import {modulo, RES} from "./i.js"; // ./dump.js
import {UIUtils} from "./i.js"; // ./ui/utils.js
import {THING_TYPES} from "./i.js"; // ./thing.js

// TODO: Integrate this with scanner
export class Grid extends Graphics {
	static LINE_STYLE = {
		width: 1,
		color: 0xdddddd
	};

	// Create a grid
	// Create only one grid per space.
	constructor() {
		super();
	}

	// Redraw the grid to match offset
	// screenSize is a vector containing the screen size, in pixels.
	// pixelsPerFlare is a real number. offset is the position of the origin.
	redraw(screenSize, pixelsPerFlare, offset) {
		this.clear();
		this.lineStyle(Grid.LINE_STYLE);
		let xStart = modulo(offset.x, pixelsPerFlare);
		for (let x = xStart; x < screenSize.x; x += pixelsPerFlare) {
			this.moveTo(x, 0)
				.lineTo(x, screenSize.y);
		}
		let yStart = modulo(offset.y, pixelsPerFlare);
		for (let y = yStart; y < screenSize.y; y += pixelsPerFlare) {
			this.moveTo(0, y)
				.lineTo(screenSize.x, y);
		}
	}
}



const BAR_WIDTH = 30;
const BAR_HEIGHT = 3;
const BAR_COLOR_BACK = 0xff0000;
const BAR_COLOR_FRONT = 0x00ff00;

class Bar extends Graphics {
	// Create a new bar
	// Pass pos of a thing as parentPos, and the bar will store a reference to
	// that position and track the thing's movement. Other params: offset is
	// y-offset from parentPos, val is the current value of the bar, maxVal is
	// the max value of the bar, duh.
	constructor(parentPos, offset, val, maxVal) {
		super();
		this._parentPos = parentPos;
		this._offset = offset;
		this.val = val;
		this._maxVal = maxVal;
		this.visible = false;
	}

	// Redraw the bar
	// This function should be called every time the position or value of the
	// bar changes. Probably just run it every tick to be safe.
	redraw() {
		let x = this._parentPos.x - BAR_WIDTH / 2;
		let y = this._parentPos.y + this._offset;
		this.clear();
		this.beginFill(BAR_COLOR_BACK);
		this.drawRect(x, y, BAR_WIDTH, BAR_HEIGHT);
		this.beginFill(BAR_COLOR_FRONT);
		this.drawRect(x, y, (this.val / this._maxVal) * BAR_WIDTH, BAR_HEIGHT);
		this.endFill();
	}

	selfDestruct() {
		this.parent.removeChild(this);
	}

	// There is no function to change val, just change it directly.
}

export class Scanner extends Container {
	// Create a scanner
	// space is a reference to space that this scanner is a part of.
	constructor(space) {
		super();

		// Save the reference to space
		this._space = space;

		this._bars = new Container();
		this.addChild(this._bars);
		this._arrows = new Container();
		this.addChild(this._arrows);

		this.readyToDock = new Text(
			"Press (X) to dock to the station",
			UIUtils.getFont(0xdddddd, {fontSize: 24})
		);
		this.readyToDock.anchor.set(0.5, 0.5);
		this.addChild(this.readyToDock);
	}

	// Create a new bar and add it to the scanner
	// Pass pos of a thing as parentPos, and the bar will store a reference to
	// that position and track the thing's movement. Other params: offset is
	// y-offset from parentPos, val is the current value of the bar, maxVal is
	// the max value of the bar, duh. This function returns the bar added.
	addBar(parentPos, offset, val, maxVal) {
		return this._bars.addChild(new Bar(parentPos, offset, val, maxVal));
	}

	// Destroy bar
	removeBar(bar) {
		this._bars.removeChild(bar);
	}

	// Redraw all bars
	// This function should be called every time the position or value of any
	// bar changes. Probably just run it every tick to be safe. screenSize is a
	// vector in pixels. pixelsPerFlare is a real number.
	redraw(screenSize, pixelsPerFlare) {
		// Redraw bars
		for (let i = 0; i < this._bars.children.length; ++i) {
			this._bars.children[i].redraw();
		}

		// Redraw arrows

		// Get list of target ids from the gps
		let gpsArrows = Array.from(this._space.game.unicenter.gps.arrows);

		// Correct the number of arrows
		// TODO: Use UIUtils.cullElements().
		while (this._arrows.children.length > gpsArrows.length) {
			this._arrows.removeChildAt(0);
		}
		while (this._arrows.children.length < gpsArrows.length) {
			let arrow = new Sprite(RES["images/arrow.svg"].texture);
			arrow.anchor.set(0.5, 1);
			this._arrows.addChild(arrow);
		}

		let player = this._space.player;

		// Put arrows in the correct spots
		for (let i = 0; i < gpsArrows.length; ++i) {
			let arrow = this._arrows.children[i];
			arrow.visible = true;

			arrow.scale.set(0.003 * pixelsPerFlare);

			let target = this._space.requestThingById(gpsArrows[i]);
			if (target === null) {
				arrow.visible = false;
				continue;
			}

			let angle = Vector.diff(target.pos, player.pos).dir();

			// Arrow sprite points up by default, correct for that
			arrow.rotation = angle + Math.PI / 2;

			// Distance from the center of the player to the back of the arrow
			let distanceToArrow = 0.55 * pixelsPerFlare;

			arrow.position.copyFrom(Vector.sum(
				player.display.position,
				new Vector(
					distanceToArrow * Math.cos(angle),
					distanceToArrow * Math.sin(angle)
				)
			));

			// Distance from the player to the target
			let distanceToTarget = Vector.diff(
				target.pos,
				player.pos
			).mag();

			if (target.TYPE === THING_TYPES.STATION) {
				arrow.visible = (distanceToTarget > 3);
				arrow.tint = 0x00ff00;
			} else {
				arrow.tint = (distanceToTarget < 10) ? 0xff0000 : 0xdddddd;
			}
		}

		// Redraw the ready-to-dock hint
		this.readyToDock.visible = this._space.canDock;
		this.readyToDock.position.copyFrom(
			new Vector(screenSize.x / 2, screenSize.y / 4).round()
		);
	}
}
